import React, { createContext, useEffect, useState } from "react";
import { GAME_STAGES, DEFAULT_ROOM } from "../constants/gameStages";
import socket from "../socket/socket";

/*
 * SocketProvider component initializes the socket connection and provides
 * the socket instance and game state to the rest of the application.
 * It listens for various socket events and updates the context accordingly.
 * Components can use the `useSocket` hook to access the context.
 **/
export const SocketContext = createContext();

export const SocketProvider = ({ children }) => {
  const [role, setRole] = useState(null);
  const [gameStage, setGameStage] = useState(null);
  const [showPausedOverlay, setShowPausedOverlay] = useState(false);
  const [round, setRound] = useState(1);
  const [playMode, setPlayMode] = useState(null);
  const [userId, setUserIdState] = useState(null);
  // badgeRole freezes at game-start so the badge colour doesn't flip on the
  // mid-round swap that runs after the guesser submits.
  const [badgeRole, setBadgeRole] = useState(null);
  const [consentSeen, setConsentSeen] = useState(false);
  const [roomFull, setRoomFull] = useState(false);
  const gameActiveRef = React.useRef(false);
  const roleRef = React.useRef(null);

  useEffect(() => {
    console.log("👟 Initializing socket listeners");

    const handleConnect = () => {
      console.log("✅ Connected:", socket.id);
      socket.emit("join", { userId: socket.id, room: DEFAULT_ROOM });
      socket.emit("get_game_stage", { room: DEFAULT_ROOM });
    };

    const handleRoleAssigned = ({ role }) => {
      console.log("🎭 Role assigned:", role, "| Previous role:", roleRef.current, "| Socket ID:", socket.id, "| gameActive:", gameActiveRef.current);
      setRole(role);
      roleRef.current = role;
      // Only update badge colour before the game is active (initial / lobby assignment).
      // Once the game is running the badge colour is frozen until next round.
      if (!gameActiveRef.current) {
        setBadgeRole(role);
      }
      console.log("✅ Role state updated to:", role);
    };

    const handleConnectError = (err) => {
      console.error("Socket error:", err.message);
    };

    const handleGameStageUpdated = ({ stage }) => {
      setGameStage(stage);
    };

    const handleBothReady = () => {
      console.log("Both players connected — ready to proceed");
      setShowPausedOverlay(false);
    };

    const handlePauseGame = ({ reason }) => {
      console.log("Game paused:", reason);
      setGameStage(GAME_STAGES.PAUSE);
      setShowPausedOverlay(true);
    };

    const handlePlayerReturned = () => {
      console.log("Received return trigger from other player");
      setShowPausedOverlay(true);
      setGameStage(GAME_STAGES.PAUSE);
    };

    const handleRoundUpdated = (data) => {
      setRound(data.round);
    };

    const handleRequestRejoin = () => {
      console.log("🔄 Server requested rejoin, re-emitting join");
      socket.emit("join", { userId: socket.id, room: DEFAULT_ROOM });
    };

    const handleRolesSwapped = ({ describer, guesser }) => {
      const mySocketId = socket.id;
      const newRole =
        mySocketId === describer
          ? "describer"
          : mySocketId === guesser
          ? "guesser"
          : null;

      if (newRole) {
        setRole(newRole);
        roleRef.current = newRole;
        console.log("🔄 Role swapped! New role:", newRole);
      }
    };

    const handlePlayerIdAssigned = ({ playerId }) => {
      console.log("🏷️ Player ID assigned:", playerId);
      setUserIdState(playerId);
    };

    const handlePlayModeUpdated = ({ playMode: newMode }) => {
      console.log("🎮 Play mode updated:", newMode);
      setPlayMode(newMode);
      if (newMode === "private") {
        setUserIdState(null);
      }
    };

    // game_started: freeze badge colour to the role at round-start; mark game active
    const handleGameStartedBadge = () => {
      gameActiveRef.current = true;
    };

    // next_round_confirmed: new round about to begin — the role swap already happened
    // (after guesser_submit), so roleRef already holds the next-round role.
    const handleNextRoundConfirmedBadge = () => {
      setBadgeRole(roleRef.current);
      gameActiveRef.current = false;
    };

    // game_reset fires when a player quits (ReturnHomeButton / player_left_game).
    // new_game_confirmed fires when both players vote "Quit Game".
    // Both mean the game session is over — clear the player ID so the badge disappears.
    const handleGameResetId = () => {
      setUserIdState(null);
      setBadgeRole(null);
      setConsentSeen(false);
      gameActiveRef.current = false;
    };

    const handleNewGameConfirmedId = () => {
      setUserIdState(null);
      setBadgeRole(null);
      setConsentSeen(false);
      gameActiveRef.current = false;
    };

    // Register all listeners with named references for clean removal
    socket.on("connect", handleConnect);
    socket.on("role_assigned", handleRoleAssigned);
    socket.on("connect_error", handleConnectError);
    socket.on("game_stage_updated", handleGameStageUpdated);
    socket.on("both_ready", handleBothReady);
    socket.on("pause_game", handlePauseGame);
    socket.on("player-returned", handlePlayerReturned);
    socket.on("round_updated", handleRoundUpdated);
    socket.on("roles_swapped", handleRolesSwapped);
    socket.on("request_rejoin", handleRequestRejoin);
    socket.on("player_id_assigned", handlePlayerIdAssigned);
    socket.on("play_mode_updated", handlePlayModeUpdated);
    socket.on("game_reset", handleGameResetId);
    socket.on("new_game_confirmed", handleNewGameConfirmedId);
    socket.on("game_started", handleGameStartedBadge);
    socket.on("next_round_confirmed", handleNextRoundConfirmedBadge);

    const handleRoomFull = () => setRoomFull(true);
    socket.on("room_full", handleRoomFull);

    // If socket is already connected (e.g. StrictMode re-mount), emit join immediately
    if (socket.connected) {
      console.log("✅ Socket already connected on mount:", socket.id);
      socket.emit("join", { userId: socket.id, room: DEFAULT_ROOM });
      socket.emit("get_game_stage", { room: DEFAULT_ROOM });
    }

    return () => {
      socket.off("connect", handleConnect);
      socket.off("role_assigned", handleRoleAssigned);
      socket.off("connect_error", handleConnectError);
      socket.off("game_stage_updated", handleGameStageUpdated);
      socket.off("both_ready", handleBothReady);
      socket.off("pause_game", handlePauseGame);
      socket.off("player-returned", handlePlayerReturned);
      socket.off("round_updated", handleRoundUpdated);
      socket.off("roles_swapped", handleRolesSwapped);
      socket.off("request_rejoin", handleRequestRejoin);
      socket.off("player_id_assigned", handlePlayerIdAssigned);
      socket.off("play_mode_updated", handlePlayModeUpdated);
      socket.off("game_reset", handleGameResetId);
      socket.off("new_game_confirmed", handleNewGameConfirmedId);
      socket.off("game_started", handleGameStartedBadge);
      socket.off("next_round_confirmed", handleNextRoundConfirmedBadge);
      socket.off("room_full", handleRoomFull);
    };
  }, []);

  return (
    <SocketContext.Provider
      value={{
        socket,
        role,
        gameStage,
        round,
        showPausedOverlay,
        setShowPausedOverlay,
        playMode,
        setPlayMode,
        userId,
        badgeRole,
        consentSeen,
        setConsentSeen,
        roomFull,
      }}
    >
      {children}
    </SocketContext.Provider>
  );
};
